import Head from 'next/head';
import type { AppProps } from 'next/app';
import type { NextComponentType } from 'next';
import { ConfigProvider, Spin } from 'antd';
import { Provider } from 'react-redux';
import ReactGA from 'react-ga';
import { store } from '../store';
import { AuthProvider } from '../context/auth';
import { AuthGuard } from '../components/Layouts/AuthGuard';
import ToastLayout from '../components/Layouts/ToastLayout';
import Loader from '../components/Common/Loader';
import { hotjar } from 'react-hotjar';
import '../styles/globals.css';
// import 'antd/dist/antd.css';

import { useRouter } from 'next/router';
import PagesLayout from '@/components/Layouts/PagesLayout';
import zipy from 'zipyai';
import { DashboardProvider } from '@/context/dashboard';
import { HeroUIProvider, ToastProvider } from '@heroui/react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../config/microsoftAuthConfig';

Spin.setDefaultIndicator(<Loader />);

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);
msalInstance.initialize();

if (
  process.env.NEXT_PUBLIC_LOCATION === 'USA' &&
  typeof window !== 'undefined'
) {
  hotjar.initialize(3179575, 6);
  ReactGA.initialize('G-Z9CQXMB3KN');
  zipy.init('ea1bf54d');
} else if (
  process.env.NEXT_PUBLIC_LOCATION === 'EUROPE' &&
  typeof window !== 'undefined'
) {
  hotjar.initialize(5070333, 6);
  ReactGA.initialize('G-Z9CQXMB3KN');
  zipy.init('f1455a43');
}

type CustomAppProps = AppProps & {
  Component: NextComponentType & {
    accessType: 'authenticated' | 'anonymous' | null;
  };
};

function MyApp({ Component, pageProps }: CustomAppProps) {
  const { accessType = null } = Component;
  const router = useRouter();
  return (
    <>
      <Head>
        <title>Enumerate AI</title>
      </Head>
      <Provider store={store}>
        <MsalProvider instance={msalInstance}>
          <AuthProvider>
            <ToastLayout>
              <ConfigProvider
                theme={{
                  token: {
                    fontFamily: 'Nunito Sans, sans-serif',
                    colorPrimary: '#5579F2',
                  },
                }}
              >
                <HeroUIProvider>
                  <ToastProvider
                    placement="top-right"
                    toastProps={{
                      classNames: {
                        content: 'gap-x-2',
                      },
                    }}
                    toastOffset={16}
                  />
                  <AuthGuard accessType={accessType}>
                    {router.pathname.startsWith('/setting') ||
                    router.pathname.startsWith('/billing') ? (
                      <PagesLayout>
                        <Component {...pageProps} />
                      </PagesLayout>
                    ) : router.pathname.startsWith('/dashboard') ? (
                      <DashboardProvider>
                        <Component {...pageProps} />
                      </DashboardProvider>
                    ) : (
                      <Component {...pageProps} />
                    )}
                  </AuthGuard>
                </HeroUIProvider>
              </ConfigProvider>
            </ToastLayout>
          </AuthProvider>
        </MsalProvider>
      </Provider>
    </>
  );
}

export default MyApp;
